import React, { useCallback, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from '@react-google-maps/api';
import { IconButton, Tooltip, Box, CircularProgress, Typography } from '@mui/material';
import { MdMyLocation } from 'react-icons/md';

const containerStyle = {
  width: '100%',
  height: '350px',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const defaultCenter = {
  lat: 20.5937, // Center of India
  lng: 78.9629
};

function MapPicker({ lat, lng, onChange }) {
  const [autocomplete, setAutocomplete] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLocating, setIsLocating] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
  });

  const markerPosition = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : null;

  const handleMapClick = useCallback((event) => {
    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    onChange(newPosition);
    setSearchValue(`${newPosition.lat.toFixed(6)}, ${newPosition.lng.toFixed(6)}`);
  }, [onChange]);

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          onChange(newPos);
          setSearchValue(`${newPos.lat.toFixed(6)}, ${newPos.lng.toFixed(6)}`);
          setIsLocating(false);
        },
        (error) => {
          console.error("Error getting location", error);
          setIsLocating(false);
          alert("Could not get your current location. Please ensure location permissions are granted.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  const onLoad = useCallback((autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const newPosition = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        onChange(newPosition);
        setSearchValue(place.formatted_address || `${newPosition.lat}, ${newPosition.lng}`);
      }
    }
  }, [autocomplete, onChange]);

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <Box p={3} sx={{ textAlign: 'center', bgcolor: '#fff3f3', border: '1px dashed red', borderRadius: '12px' }}>
        <Typography color="error" fontWeight={700}>Google Maps API Key Missing</Typography>
        <Typography variant="body2">Please check your .env file and restart the server.</Typography>
      </Box>
    );
  }

  if (loadError) {
    return <Box p={2} sx={{ color: 'error.main', bgcolor: '#fff' }}>Error loading Google Maps. Please check your API key.</Box>;
  }

  return isLoaded ? (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        left: 10, 
        right: 10, 
        zIndex: 10,
        display: 'flex',
        gap: 1
      }}>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
          >
            <input
              type="text"
              placeholder="Search for your store location..."
              style={{
                boxSizing: 'border-box',
                border: '1px solid #ddd',
                width: '100%',
                height: '45px',
                padding: '0 12px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                fontSize: '13.5px',
                outline: 'none',
                backgroundColor: '#fff',
                fontFamily: 'Mulish, sans-serif'
              }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </Autocomplete>
        </Box>
        
        <Tooltip title="Locate my current position">
          <IconButton 
            onClick={handleLocateMe}
            disabled={isLocating}
            sx={{ 
              bgcolor: '#fff', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': { bgcolor: '#f5f5f5' },
              width: 45,
              height: 45
            }}
          >
            {isLocating ? <CircularProgress size={20} /> : <MdMyLocation color="#1976d2" />}
          </IconButton>
        </Tooltip>
      </Box>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={markerPosition || defaultCenter}
        zoom={markerPosition ? 16 : 5}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false
        }}
      >
        {markerPosition && <Marker position={markerPosition} />}
      </GoogleMap>
      
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Tip: You can click anywhere on the map to pin the exact location or search above.
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: '12px' }}>
      <CircularProgress size={30} />
      <Typography sx={{ ml: 2, color: 'text.secondary' }}>Initializing Map...</Typography>
    </Box>
  );
}

export default React.memo(MapPicker);